import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';

export type ThemePreference = 'light' | 'dark' | 'system';
export type EffectiveTheme = 'light' | 'dark';

const STORAGE_KEY = 'kiln-theme';

export const useThemeStore = defineStore('theme', () => {
  const preference = ref<ThemePreference>(getStoredPreference());
  const systemPrefersDark = ref(getSystemPreference());

  const effectiveTheme = computed<EffectiveTheme>(() => {
    if (preference.value === 'system') {
      return systemPrefersDark.value ? 'dark' : 'light';
    }
    return preference.value;
  });

  function getStoredPreference(): ThemePreference {
    if (typeof window === 'undefined') return 'system';
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
    return 'system';
  }

  function getSystemPreference(): boolean {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  function setPreference(newPreference: ThemePreference) {
    preference.value = newPreference;
    localStorage.setItem(STORAGE_KEY, newPreference);
  }

  function cycleTheme() {
    const order: ThemePreference[] = ['system', 'light', 'dark'];
    const currentIndex = order.indexOf(preference.value);
    const nextIndex = (currentIndex + 1) % order.length;
    setPreference(order[nextIndex]);
  }

  function applyTheme(theme: EffectiveTheme) {
    document.documentElement.setAttribute('data-theme', theme);
  }

  function initTheme() {
    // Apply initial theme
    applyTheme(effectiveTheme.value);

    // Listen for system preference changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      systemPrefersDark.value = e.matches;
    };
    mediaQuery.addEventListener('change', handleChange);

    // Watch for effective theme changes and apply them
    watch(effectiveTheme, (newTheme) => {
      applyTheme(newTheme);
    });
  }

  return {
    preference,
    effectiveTheme,
    systemPrefersDark,
    setPreference,
    cycleTheme,
    initTheme,
  };
});
